'use client'

import { useEffect, useMemo, useState } from 'react'
import { baseDashboardClasses } from '../BaseDashboard'
import { apiGet, useCatalog } from './entriesApi'
import { BrandVocabularyEditor } from './BrandVocabularyEditor'
import { SearchableSelect } from './SearchableSelect'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// Administration › Brand Vocabulary. The words each brand is sourced with,
// as its own page: pick a platform, see whether it has a saved row or still
// runs on the built-in config, edit, Suggest with AI, Preview, Save. Domain
// Candidates links here with the platform preselected (?platform_id=).
//
// Editor-only by placement -- Administration is never granted to tenants --
// and the backend refuses non-Editors on every /autodomains/brands route
// regardless.

type SavedRow = { platform_id: number; brand_name: string | null; is_active: boolean; updated_at: string; updated_by: string | null }

export function BrandVocabularyView({ theme }: { theme: ProcessedTheme }) {
  const { isDark, border, panelBg, muted, text } = baseDashboardClasses(theme)
  const { catalog, loading: catalogLoading } = useCatalog()
  const [platformId, setPlatformId] = useState<number | ''>('')
  const [saved, setSaved] = useState<Map<number, SavedRow>>(new Map())
  const [savedLoaded, setSavedLoaded] = useState(false)

  // Deep link from Domain Candidates: /entries/brand-vocabulary?platform_id=23
  useEffect(() => {
    try {
      const id = Number(new URLSearchParams(window.location.search).get('platform_id'))
      if (Number.isFinite(id) && id > 0) setPlatformId(id)
    } catch { /* no window in SSR */ }
  }, [])

  const loadSaved = async () => {
    const r = await apiGet<SavedRow[]>('/auto-domains/brands')
    if (r.success && r.data) setSaved(new Map(r.data.map((row) => [row.platform_id, row])))
    setSavedLoaded(true)
  }
  useEffect(() => { void loadSaved() }, [])

  const platforms = useMemo(
    () => [...catalog.platforms].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [catalog.platforms],
  )
  const platform = platforms.find((p) => p.id === platformId)

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className={`font-mono text-[10px] uppercase tracking-[0.28em] ${muted}`}>Administration · Brand Vocabulary</div>
        <h1 className={`mt-1 text-lg font-semibold ${text}`}>Brand Vocabulary</h1>
        <p className={`mt-1 max-w-3xl text-[12px] ${muted}`}>
          The words each brand is sourced with: what is searched, what is combined with lure words, the brand&apos;s own domains,
          and the narrative scammers use for it. Saved here, it applies to the next sourcing run — no deploy. Built-in
          configurations still apply to any field left empty.
        </p>
      </div>

      <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-sm border px-4 py-3 ${border} ${panelBg}`}>
        <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Platform</span>
        <div className={`w-64 ${catalogLoading ? 'pointer-events-none opacity-60' : ''}`}>
          <SearchableSelect theme={theme} value={platformId === '' ? undefined : platformId} clearable={false}
            options={platforms.map((p) => ({ id: p.id, name: saved.has(p.id) ? `${p.name}  ● saved` : p.name }))}
            placeholder="Pick a platform…" searchPlaceholder="Search platforms…"
            onChange={(id) => setPlatformId(id ? Number(id) : '')} />
        </div>
        {savedLoaded && (
          <span className={`font-mono text-[11px] ${muted}`}>
            {saved.size} platform{saved.size === 1 ? '' : 's'} with a saved vocabulary · the rest use the built-in configuration or have none
          </span>
        )}
        {platform && (
          <a href={`/entries/token-chain?platform_id=${platform.id}`} className={`font-mono text-[10px] uppercase tracking-widest ${muted} hover:text-signal`}>
            see its token chain →
          </a>
        )}
        {platform && (
          <span className={`ml-auto font-mono text-[10px] uppercase tracking-widest text-muted-foreground`}>
            {saved.has(platform.id) ? `saved ${new Date(saved.get(platform.id)!.updated_at).toLocaleString()}` : 'no saved row'}
          </span>
        )}
      </div>

      {platformId === '' ? (
        <div className={`rounded-sm border px-4 py-10 text-center font-mono text-[11px] ${border} ${panelBg} ${muted}`}>
          Pick a platform to edit its vocabulary.
        </div>
      ) : (
        <BrandVocabularyEditor key={platformId} theme={theme} platformId={platformId} platformName={platform?.name ?? null} />
      )}
    </div>
  )
}
